/**
 * Newsletter form: pre-fill from footer redirect + merge optional fields into customer tags.
 */
const DS_NEWSLETTER_RETURN_KEY = 'ds-newsletter-return-to';
const DS_NEWSLETTER_SUCCESS_KEY = 'ds-newsletter-submit-success';
const DS_NEWSLETTER_PREFILL_PARAMS = [
  'first_name',
  'last_name',
  'email',
  'country',
  'birthdate',
  'customer_posted',
];

class DsNewsletterCountrySelect extends HTMLElement {
  /** @type {HTMLSelectElement | null} */
  #select = null;

  /** @type {HTMLButtonElement | null} */
  #trigger = null;

  /** @type {HTMLElement | null} */
  #panel = null;

  /** @type {HTMLElement | null} */
  #valueEl = null;

  /** @type {HTMLButtonElement[]} */
  #options = [];

  connectedCallback() {
    this.#select = this.querySelector('[data-ds-newsletter-field="country"]');
    this.#trigger = this.querySelector('.ds-newsletter-country__trigger');
    this.#panel = this.querySelector('.ds-newsletter-country__panel');
    this.#valueEl = this.querySelector('[data-ds-newsletter-country-value]');

    if (!(this.#select instanceof HTMLSelectElement)) return;
    if (!(this.#trigger instanceof HTMLButtonElement)) return;
    if (!(this.#panel instanceof HTMLElement)) return;
    if (!(this.#valueEl instanceof HTMLElement)) return;

    this.#select.querySelector('option[value="---"]')?.remove();
    this.#buildOptions();
    this.syncFromSelect();

    this.#trigger.addEventListener('click', this.#onTriggerClick);
    this.#panel.addEventListener('click', this.#onPanelClick);
    this.#trigger.addEventListener('keydown', this.#onTriggerKeydown);
    this.#panel.addEventListener('keydown', this.#onPanelKeydown);
    document.addEventListener('click', this.#onDocumentClick);
    document.addEventListener('keydown', this.#onDocumentKeydown);
  }

  disconnectedCallback() {
    this.#trigger?.removeEventListener('click', this.#onTriggerClick);
    this.#panel?.removeEventListener('click', this.#onPanelClick);
    this.#trigger?.removeEventListener('keydown', this.#onTriggerKeydown);
    this.#panel?.removeEventListener('keydown', this.#onPanelKeydown);
    document.removeEventListener('click', this.#onDocumentClick);
    document.removeEventListener('keydown', this.#onDocumentKeydown);
  }

  syncFromSelect() {
    if (!(this.#select instanceof HTMLSelectElement)) return;
    if (!(this.#valueEl instanceof HTMLElement)) return;

    const selectedOption = this.#select.selectedOptions[0];
    const placeholder = this.#select.querySelector('option[value=""]')?.textContent?.trim() ?? '';

    if (!selectedOption?.value) {
      this.#valueEl.textContent = placeholder;
      this.#valueEl.classList.add('is-placeholder');
      this.#updateOptionStates('');
      return;
    }

    this.#valueEl.textContent = selectedOption.textContent?.trim() ?? selectedOption.value;
    this.#valueEl.classList.remove('is-placeholder');
    this.#updateOptionStates(selectedOption.value);
  }

  /** @param {string} value */
  setValue(value) {
    if (!(this.#select instanceof HTMLSelectElement)) return;

    const normalizedValue = value.trim();
    if (normalizedValue === '' || normalizedValue === '---') {
      this.#select.value = '';
      this.syncFromSelect();
      return;
    }

    const matchingOption = [...this.#select.options].find((option) => option.value === normalizedValue);
    if (!matchingOption) return;

    this.#select.value = normalizedValue;
    this.syncFromSelect();
  }

  #buildOptions() {
    if (!(this.#select instanceof HTMLSelectElement)) return;
    if (!(this.#panel instanceof HTMLElement)) return;

    this.#panel.replaceChildren();
    this.#options = [];

    for (const option of this.#select.options) {
      if (!option.value || option.value === '---' || option.disabled) continue;

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'ds-newsletter-country__option';
      button.role = 'option';
      button.tabIndex = -1;
      button.dataset.value = option.value;
      button.textContent = option.textContent?.trim() ?? option.value;
      button.id = `${this.#panel.id}-option-${option.value.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`;

      this.#panel.appendChild(button);
      this.#options.push(button);
    }
  }

  /** @param {string} value */
  #updateOptionStates(value) {
    for (const option of this.#options) {
      const isSelected = option.dataset.value === value;
      option.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      option.tabIndex = isSelected ? 0 : -1;
    }
  }

  /** @param {string} value */
  #selectValue(value) {
    if (!(this.#select instanceof HTMLSelectElement)) return;

    this.#select.value = value;
    this.syncFromSelect();
    this.#close();
    this.#trigger?.focus();
  }

  #open() {
    if (!(this.#trigger instanceof HTMLButtonElement)) return;
    if (!(this.#panel instanceof HTMLElement)) return;

    this.#panel.hidden = false;
    this.#trigger.setAttribute('aria-expanded', 'true');

    const selectedOption = this.#options.find(
      (option) => option.getAttribute('aria-selected') === 'true'
    );
    const focusTarget = selectedOption ?? this.#options[0];

    if (focusTarget instanceof HTMLButtonElement) {
      for (const option of this.#options) {
        option.tabIndex = option === focusTarget ? 0 : -1;
      }
      focusTarget.focus();
    }
  }

  #close() {
    if (!(this.#trigger instanceof HTMLButtonElement)) return;
    if (!(this.#panel instanceof HTMLElement)) return;

    this.#panel.hidden = true;
    this.#trigger.setAttribute('aria-expanded', 'false');
  }

  /** @param {MouseEvent} event */
  #onTriggerClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!(this.#trigger instanceof HTMLButtonElement)) return;

    if (this.#trigger.getAttribute('aria-expanded') === 'true') {
      this.#close();
      return;
    }

    this.#open();
  };

  /** @param {MouseEvent} event */
  #onPanelClick = (event) => {
    const target = event.target;

    if (!(target instanceof Element)) return;

    const option = target.closest('.ds-newsletter-country__option');

    if (!(option instanceof HTMLButtonElement)) return;

    const value = option.dataset.value;
    if (!value) return;

    this.#selectValue(value);
  };

  /** @param {KeyboardEvent} event */
  #onTriggerKeydown = (event) => {
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.#open();
        break;
      case 'Escape':
        this.#close();
        break;
      default:
        break;
    }
  };

  /** @param {KeyboardEvent} event */
  #onPanelKeydown = (event) => {
    if (this.#options.length === 0) return;

    const currentIndex = this.#options.findIndex((option) => option.tabIndex === 0);
    let nextIndex = currentIndex >= 0 ? currentIndex : 0;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        nextIndex = Math.min(nextIndex + 1, this.#options.length - 1);
        this.#moveFocus(nextIndex);
        break;
      case 'ArrowUp':
        event.preventDefault();
        nextIndex = Math.max(nextIndex - 1, 0);
        this.#moveFocus(nextIndex);
        break;
      case 'Home':
        event.preventDefault();
        this.#moveFocus(0);
        break;
      case 'End':
        event.preventDefault();
        this.#moveFocus(this.#options.length - 1);
        break;
      case 'Enter':
      case ' ': {
        event.preventDefault();
        const focusedOption = this.#options.find((option) => option.tabIndex === 0);
        const value = focusedOption?.dataset.value;
        if (value) this.#selectValue(value);
        break;
      }
      case 'Escape':
        event.preventDefault();
        this.#close();
        this.#trigger?.focus();
        break;
      case 'Tab':
        this.#close();
        break;
      default:
        break;
    }
  };

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    const target = event.target;

    if (!(target instanceof Node)) return;
    if (this.contains(target)) return;

    this.#close();
  };

  /** @param {KeyboardEvent} event */
  #onDocumentKeydown = (event) => {
    if (event.key !== 'Escape') return;
    if (!(this.#trigger instanceof HTMLButtonElement)) return;
    if (this.#trigger.getAttribute('aria-expanded') !== 'true') return;

    event.preventDefault();
    this.#close();
    this.#trigger.focus();
  };

  /** @param {number} index */
  #moveFocus(index) {
    for (const [optionIndex, option] of this.#options.entries()) {
      option.tabIndex = optionIndex === index ? 0 : -1;
    }

    const target = this.#options[index];
    target?.focus();
  }
}

if (!customElements.get('ds-newsletter-country-select')) {
  customElements.define('ds-newsletter-country-select', DsNewsletterCountrySelect);
}

function shouldShowDsNewsletterSuccess(form) {
  const params = new URLSearchParams(window.location.search);
  if (params.get('customer_posted') === 'true') {
    sessionStorage.removeItem(DS_NEWSLETTER_SUCCESS_KEY);
    return true;
  }

  const hash = window.location.hash.slice(1);
  if (!hash) return false;

  if (hash === 'contact_form') return true;
  if (form.id && hash === form.id) return true;

  // Legacy hash from earlier form id="DsNewsletter-…" submissions.
  return hash.startsWith('DsNewsletter-');
}

function initDsNewsletterReturnRecovery() {
  const params = new URLSearchParams(window.location.search);
  const pendingSuccess = sessionStorage.getItem(DS_NEWSLETTER_SUCCESS_KEY) === '1';

  if (!params.has('customer_posted') && !pendingSuccess) return;

  const storedReturnTo = sessionStorage.getItem(DS_NEWSLETTER_RETURN_KEY);
  if (!storedReturnTo) return;

  sessionStorage.removeItem(DS_NEWSLETTER_RETURN_KEY);
  sessionStorage.removeItem(DS_NEWSLETTER_SUCCESS_KEY);

  const returnUrl = new URL(storedReturnTo, window.location.origin);

  if (returnUrl.pathname === window.location.pathname) return;

  // After reCAPTCHA Shopify often lands on / instead of the thank-you page.
  if (window.location.pathname !== '/') return;

  window.location.replace(returnUrl.toString());
}

function initDsNewsletterFormPrefill(root = document) {
  const containers = root.querySelectorAll('[data-ds-newsletter-prefill]');
  if (containers.length === 0) return;

  const params = new URLSearchParams(window.location.search);
  if ([...params.keys()].length === 0) return;

  for (const container of containers) {
    if (!(container instanceof HTMLElement)) continue;

    for (const input of container.querySelectorAll('[data-ds-newsletter-field]')) {
      if (!(input instanceof HTMLElement)) continue;

      const key = input.dataset.dsNewsletterField;
      if (!key) continue;

      const value = params.get(key);
      if (value == null || value === '') continue;

      if (input instanceof HTMLInputElement) {
        input.value = value;
        continue;
      }

      if (input instanceof HTMLSelectElement) {
        input.value = value;
        const countrySelect = input.closest('ds-newsletter-country-select');
        if (countrySelect instanceof DsNewsletterCountrySelect) {
          countrySelect.setValue(value);
        }
      }
    }
  }
}

function initDsNewsletterPrefillUrlCleanup(root = document) {
  const containers = root.querySelectorAll('[data-ds-newsletter-prefill]');
  if (containers.length === 0) return;

  const url = new URL(window.location.href);
  let dirty = false;

  for (const key of DS_NEWSLETTER_PREFILL_PARAMS) {
    if (!url.searchParams.has(key)) continue;
    url.searchParams.delete(key);
    dirty = true;
  }

  if (url.hash) {
    url.hash = '';
    dirty = true;
  }

  if (!dirty) return;

  const cleanUrl = url.pathname + url.search;
  window.history.replaceState(null, '', cleanUrl);
}

function revealDsNewsletterSuccessMessage(message) {
  requestAnimationFrame(() => {
    message.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  });
}

function renderDsNewsletterSuccessMessage(form, container) {
  const existingMessage = form.querySelector('.ds-email-signup__message.ds-message--success');
  if (existingMessage instanceof HTMLElement) {
    revealDsNewsletterSuccessMessage(existingMessage);
    return;
  }

  const text = container.dataset.dsNewsletterSuccessMessage?.trim();
  if (!text) return;

  const message = document.createElement('div');
  message.className = 'ds-message ds-message--success ds-email-signup__message';
  message.setAttribute('role', 'status');
  message.textContent = text;
  form.appendChild(message);
  revealDsNewsletterSuccessMessage(message);
}

function initDsNewsletterFormSuccessMessage(root = document) {
  for (const container of root.querySelectorAll('[data-ds-newsletter-prefill]')) {
    if (!(container instanceof HTMLElement)) continue;

    const form = container.querySelector('form.ds-email-signup__form');
    if (!(form instanceof HTMLFormElement)) continue;
    if (!shouldShowDsNewsletterSuccess(form)) continue;

    renderDsNewsletterSuccessMessage(form, container);
  }
}

function initDsNewsletterFormSubmit(root = document) {
  const containers = root.querySelectorAll('[data-ds-newsletter-prefill]');
  if (containers.length === 0) return;

  for (const container of containers) {
    if (!(container instanceof HTMLElement)) continue;

    const form = container.querySelector('form.ds-email-signup__form');
    if (!(form instanceof HTMLFormElement)) continue;
    if (form.dataset.dsNewsletterSubmitBound === 'true') continue;

    form.dataset.dsNewsletterSubmitBound = 'true';

    form.addEventListener('submit', () => {
      const successUrl = container.dataset.dsNewsletterSuccessUrl?.trim();
      const returnToInput = form.querySelector('input[name="return_to"]');
      const returnToFromForm =
        returnToInput instanceof HTMLInputElement ? returnToInput.value.trim() : '';
      const fallbackReturnTo = `${window.location.pathname}${window.location.search}`;

      sessionStorage.setItem(
        DS_NEWSLETTER_RETURN_KEY,
        successUrl || returnToFromForm || fallbackReturnTo
      );
      sessionStorage.setItem(DS_NEWSLETTER_SUCCESS_KEY, '1');

      const tagsInput = form.querySelector('[data-ds-newsletter-tags]');
      if (!(tagsInput instanceof HTMLInputElement)) return;

      const tags = ['newsletter'];

      const countrySelect = form.querySelector('[data-ds-newsletter-field="country"]');
      if (countrySelect instanceof HTMLSelectElement) {
        const country = countrySelect.value.trim();
        if (country !== '' && country !== '---') {
          tags.push(`country:${country}`);
        }
      }

      const birthInput = form.querySelector('[data-ds-newsletter-field="birthdate"]');
      if (birthInput instanceof HTMLInputElement && birthInput.value !== '') {
        tags.push(`birthdate:${birthInput.value}`);
      }

      tagsInput.value = tags.join(', ');
    });
  }
}

function initDsNewsletterForm(root = document) {
  initDsNewsletterReturnRecovery();
  initDsNewsletterFormPrefill(root);
  initDsNewsletterFormSuccessMessage(root);
  initDsNewsletterPrefillUrlCleanup(root);
  initDsNewsletterFormSubmit(root);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initDsNewsletterForm(), { once: true });
} else {
  initDsNewsletterForm();
}
